import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { PersonalAccountMenuFindInterface } from '@zaoblako/shared/modules/security/dtos/personal-account/menu/personal-account-menu-find.dto';
import type { PersonalAccountMenuFormInterface } from '@zaoblako/shared/modules/security/dtos/personal-account/menu/personal-account-menu-form.dto';

import { cloneDeep, isNil } from 'lodash';
import { map, shareReplay, tap } from 'rxjs/operators';
import type { Observable } from 'rxjs';

/** Provides personal account mode menu reading and writing */
@Injectable({ providedIn: 'root' })
export class PersonalAccountMenuService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = '/basis/client/personal-account-menu';

  /**
   * Session snapshot for personal account screens.
   * Dropped on logout, so the next session refetches. Admin reads pass `noCache` and skip this slot.
   */
  private cachedMenu: Observable<PersonalAccountMenuFindInterface> | null = null;

  /**
   * Get personal account mode menu
   *
   * @param [noCache] If `true` then in-memory cache will be ignored
   *
   * @returns `Observable` with menu tree
   */
  public getMenu(noCache?: boolean): Observable<PersonalAccountMenuFindInterface> {
    if (noCache) {
      return this.http.get<PersonalAccountMenuFindInterface>(this.baseUrl);
    }

    if (!isNil(this.cachedMenu)) {
      return this.cachedMenu;
    }

    let result: Observable<PersonalAccountMenuFindInterface>;
    result = this.http
      .get<PersonalAccountMenuFindInterface>(this.baseUrl)
      .pipe(
        tap({
          error: () => {
            if (this.cachedMenu === result) {
              this.cachedMenu = null;
            }
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        map((menu) => cloneDeep(menu))
      );
    this.cachedMenu = result;

    return result;
  }

  /**
   * Set personal account mode menu
   *
   * @param body New menu
   *
   * @returns `Observable` with updated menu tree
   */
  public setMenu(body: PersonalAccountMenuFormInterface): Observable<PersonalAccountMenuFindInterface> {
    return this.http
      .put<PersonalAccountMenuFindInterface>(this.baseUrl, body)
      .pipe(tap({
        next: () => {
          this.clearCache();
        }
      }));
  }

  /** Drop the personal account menu snapshot. The next cached read goes to the network. */
  public clearCache = (): void => {
    this.cachedMenu = null;
  };
}
